import { ApolloClient, InMemoryCache, createHttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';

function trimStr(v) {
    return typeof v === 'string' ? v.trim() : '';
}

/**
 * AppSync HTTP endpoint. Prefer full https://…amazonaws.com/graphql (us-west-1 for current APIs).
 * In dev, `/graphql` is proxied by Vite to that URL when .env sets VITE_REACT_APP_APPSYNC_GRAPHQL_ENDPOINT.
 */
function getAppSyncGraphqlUri() {
    const raw =
        trimStr(import.meta.env.VITE_REACT_APP_APPSYNC_GRAPHQL_ENDPOINT) ||
        trimStr(import.meta.env.VITE_APPSYNC_GRAPHQL_ENDPOINT) ||
        trimStr(import.meta.env.REACT_APP_APPSYNC_GRAPHQL_ENDPOINT);

    if (raw.startsWith('https://') || raw.startsWith('http://')) {
        return raw;
    }
    if (import.meta.env.DEV && (!raw || raw === '/graphql')) {
        return '/graphql';
    }
    if (raw.startsWith('/')) {
        if (typeof window !== 'undefined') {
            return `${window.location.origin}${raw}`;
        }
    }
    return raw;
}

function getGraphqlApiKey() {
    return (
        trimStr(import.meta.env.VITE_REACT_APP_GRAPHQL_API_KEY) ||
        trimStr(import.meta.env.VITE_GRAPHQL_API_KEY) ||
        trimStr(import.meta.env.REACT_APP_GRAPHQL_API_KEY) ||
        ''
    );
}

function getExpectedCognitoUserPoolId() {
    return trimStr(import.meta.env.VITE_COGNITO_USER_POOL_ID);
}

/** True when JWT was minted by the user pool AppSync is configured to trust (see .env). */
function decodeJwtPayload(jwt) {
    try {
        const part = jwt.split(".")[1];
        if (!part) return null;
        const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(
            base64.length + ((4 - (base64.length % 4)) % 4),
            "="
        );
        return JSON.parse(atob(padded));
    } catch {
        return null;
    }
}

function idTokenMatchesAppSyncPool(idToken) {
    const poolId = getExpectedCognitoUserPoolId();
    if (!idToken || !poolId) return true;
    const payload = decodeJwtPayload(idToken);
    const iss = typeof payload?.iss === "string" ? payload.iss : "";
    return iss.includes(poolId);
}

/**
 * True when AppSync will send Cognito (not API key) for owner-scoped queries.
 * Matches the authLink logic: no session, no token, or wrong-pool JWT → API key only → "Not Authorized" on @aws_cognito_user_pools-only fields.
 */
export async function hasAppSyncTrustedCognitoSession() {
    try {
        await getCurrentUser();
    } catch {
        return false;
    }
    const { tokens } = await fetchAuthSession();
    const token = tokens?.idToken?.toString();
    if (!token) return false;
    const expectedPool = getExpectedCognitoUserPoolId();
    if (expectedPool && !idTokenMatchesAppSyncPool(token)) return false;
    return true;
}

function headersWithApiKeyOnly(headers) {
    const base = { ...(headers || {}) };
    delete base.authorization;
    delete base.Authorization;
    const key = getGraphqlApiKey();
    if (key) {
        base["x-api-key"] = key;
    }
    return { headers: base };
}

const graphqlUri = getAppSyncGraphqlUri();

const apolloHttpUri =
    graphqlUri ||
    (import.meta.env.DEV ? '/graphql' : null);

if (!apolloHttpUri) {
    console.error(
        '[Apollo] Missing VITE_REACT_APP_APPSYNC_GRAPHQL_ENDPOINT (full AppSync URL, typically …appsync-api.us-west-1.amazonaws.com/graphql).'
    );
}

const httpLink = createHttpLink({
    uri:
        apolloHttpUri ??
        'https://missing-vite-react-app-appsync-graphql-endpoint.invalid/graphql',
});

const authLink = setContext(async (_, { headers }) => {
    try {
        const expectedPool = getExpectedCognitoUserPoolId();

        try {
            await getCurrentUser();
        } catch {
            return headersWithApiKeyOnly(headers);
        }

        const { tokens } = await fetchAuthSession();
        const token = tokens?.idToken?.toString();

        if (!token) {
            return headersWithApiKeyOnly(headers);
        }

        // Wrong-pool JWT → AppSync returns HTTP 401 for the whole request. Use API key for @aws_api_key fields.
        // Owner fields (e.g. `user`) need Cognito aligned with this API — set VITE_COGNITO_* overrides in aws-api Init.
        if (expectedPool && !idTokenMatchesAppSyncPool(token)) {
            if (import.meta.env.DEV) {
                console.warn(
                    "[Apollo] Cognito ID token pool does not match VITE_COGNITO_USER_POOL_ID; using API key only. " +
                        "AppSync rejects foreign-pool JWTs with 401. Add VITE_COGNITO_USER_POOL_WEB_CLIENT_ID and pool override in .env so Amplify signs in against the same pool as AppSync, or fix aws-export."
                );
            }
            return headersWithApiKeyOnly(headers);
        }

        const base = { ...(headers || {}) };
        delete base["x-api-key"];
        return {
            headers: {
                ...base,
                authorization: token,
            },
        };
    } catch (error) {
        if (import.meta.env.DEV) {
            console.warn('Auth session unavailable; using GraphQL API key.', error?.message ?? error);
        } else {
            console.error('Error in auth context link:', error);
        }
        return headersWithApiKeyOnly(headers);
    }
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache(),
  connectToDevTools: import.meta.env.NODE_ENV !== 'production'
});

export default client;
